import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getAlgoliaIndex } from '@/hooks/useAlgoliaSearch/utils';
import type { Root } from '@/hooks/useAlgoliaSearch/types';

import {
  ALGOLIA_APP_ID,
  ALGOLIA_API_KEY,
  PUBLIC_SWS_ENV,
} from '@/constants/env';

const algoliaAppId = ALGOLIA_APP_ID;
const algoliaApiKey = ALGOLIA_API_KEY;
const index =
  PUBLIC_SWS_ENV === 'dev' || PUBLIC_SWS_ENV === 'local'
    ? 'companies-dev'
    : 'companies';

export const filterHits = (hits: Root[], include: Root['type'][]): Root[] => {
  return hits.filter((result) => {
    return include.includes(result.type);
  });
};

export const useCompanySearch = () => {
  const queryClient = useQueryClient();
  const searchIndex = getAlgoliaIndex({ algoliaAppId, algoliaApiKey, index });

  const searchCompany = useCallback(
    (query: string) => {
      return queryClient.fetchQuery({
        queryKey: ['TRANSACTION_COMPANY_SEARCH', query],
        queryFn: async ({ queryKey }) => {
          const { 1: q } = queryKey;
          const response = await searchIndex.search<Root>(q, {
            highlightPostTag: ' ',
            highlightPreTag: ' ',
            restrictHighlightAndSnippetArrays: true,
          });
          if (typeof response !== 'undefined') {
            return filterHits(response.hits, ['company']);
          }
          return [];
        },
        staleTime: 3600,
      });
    },
    [queryClient, searchIndex]
  );

  const getMatchingListing = useCallback(
    (input: string | null, company?: Root) => {
      if (typeof company === 'undefined' || input === null) return null;

      const isPrimaryListingMatch = company.uniqueSymbol === input;

      if (isPrimaryListingMatch)
        return {
          name: company.name,
          exchangeCountryIso: company.exchangeCountryIso,
          uniqueSymbol: company.uniqueSymbol,
        };

      const secondaryListingMatch = company.listings.find((listing) => {
        return listing.uniqueSymbol === input;
      });
      const isSecondaryListingMatch =
        typeof secondaryListingMatch !== 'undefined';

      if (isSecondaryListingMatch)
        return {
          name: company.name,
          exchangeCountryIso: secondaryListingMatch.exchangeCountryIso,
          uniqueSymbol: secondaryListingMatch.uniqueSymbol,
        };

      // Return the primary listing as the default
      return {
        name: company.name,
        exchangeCountryIso: company.exchangeCountryIso,
        uniqueSymbol: company.uniqueSymbol,
      };
    },
    []
  );

  return {
    searchCompany,
    getMatchingListing,
  };
};
